import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ShieldCheck, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n";
import { api, imgUrl, usd } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function Bitdefender() {
  const navigate = useNavigate();
  const { t } = useLang();
  const { add } = useCart();
  const [products, setProducts] = useState([]);
  const features = t("bd_features");

  useEffect(() => {
    api.get("/products")
      .then((r) => setProducts(r.data.filter((p) => p.brand === "Bitdefender")))
      .catch(() => setProducts([]));
  }, []);

  return (
    <section
      id="bitdefender"
      data-testid="bitdefender-section"
      className="bg-[#0B1120] px-6 md:px-12 lg:px-24 py-20 lg:py-32"
    >
      <Reveal>
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#ED1C24] via-[#8f1015] to-[#0B1120] p-8 md:p-14 lg:p-20">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-[100px]" />
          <div className="grid lg:grid-cols-2 gap-12 items-center relative">
            <div>
              <div className="inline-flex items-center gap-3 border-l-2 border-white bg-white/10 backdrop-blur-sm px-5 py-2 mb-8">
                <span className="text-xs tracking-[0.25em] uppercase text-white">
                  {t("bd_badge")}
                </span>
              </div>
              <h2 className="font-display font-semibold text-white text-3xl sm:text-5xl tracking-tight leading-tight mb-6">
                {t("bd_title")}
              </h2>
              <p className="text-white/80 text-base lg:text-lg leading-relaxed mb-8">
                {t("bd_body")}
              </p>
              <ul className="space-y-3 mb-10">
                {features.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-white/90">
                    <Check size={17} className="text-[#FFC61A] shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <button
                  data-testid="bitdefender-consultation-btn"
                  onClick={() => scrollToId("contact")}
                  className="rounded-full bg-[#ED1C24] hover:bg-white hover:text-[#ED1C24] text-white font-medium px-7 py-3.5 text-sm transition-colors duration-300 border border-white/30"
                >
                  {t("bd_btn1")}
                </button>
                <button
                  data-testid="bitdefender-trial-btn"
                  onClick={() => scrollToId("contact")}
                  className="rounded-full border border-white/50 text-white hover:bg-white/10 font-medium px-7 py-3.5 text-sm transition-colors duration-300"
                >
                  {t("bd_btn2")}
                </button>
                <button
                  data-testid="bitdefender-quote-btn"
                  onClick={() => navigate("/shop")}
                  className="rounded-full bg-[#FFC61A] hover:bg-[#E5B015] text-[#0B1120] font-semibold px-7 py-3.5 text-sm transition-colors duration-300"
                >
                  {t("bd_btn3")}
                </button>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <motion.div
                initial={{ opacity: 0, rotate: 4, y: 40 }}
                whileInView={{ opacity: 1, rotate: 2, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
                className="clip-frame"
              >
                <img
                  src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?auto=format&fit=crop&w=900&q=80"
                  alt="Cybersecurity lock"
                  loading="lazy"
                  className="w-full h-[420px] object-cover shadow-[0_8px_30px_rgba(0,0,0,0.5)]"
                />
              </motion.div>
              <div className="absolute -bottom-6 -left-6 backdrop-blur-xl bg-[#0B1120]/80 border border-white/15 p-5 flex items-center gap-4">
                <ShieldCheck className="text-[#FFC61A]" size={30} />
                <div>
                  <p className="font-display text-white text-sm font-semibold">
                    {t("bd_partner")}
                  </p>
                  <p className="text-xs text-slate-400">
                    {t("bd_partner_sub")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {products.length > 0 && (
        <Reveal delay={0.1}>
          <div data-testid="bitdefender-shop" className="mt-16">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-3">{t("bd_shop_label")}</p>
                <h3 className="font-display font-semibold text-white text-2xl sm:text-3xl tracking-tight">
                  {t("bd_shop_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
                </h3>
              </div>
              <button
                data-testid="bitdefender-shop-all-btn"
                onClick={() => navigate("/shop")}
                className="text-sm text-slate-300 hover:text-[#FFC61A] border border-white/15 hover:border-[#FFC61A] px-5 py-2.5 transition-colors duration-300"
              >
                {t("bd_shop_all")} →
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p, i) => (
                <div
                  key={p.id}
                  data-testid={`bd-product-${i}`}
                  className="group bg-[#0F172A] border border-white/10 hover:border-[#ED1C24]/60 transition-colors duration-300"
                >
                  <button
                    data-testid={`bd-product-view-${i}`}
                    onClick={() => navigate(`/product/${p.id}`)}
                    className="block w-full aspect-[4/3] overflow-hidden bg-white"
                  >
                    <img
                      src={imgUrl(p.image)}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </button>
                  <div className="p-6">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">{p.category}</p>
                    <button
                      onClick={() => navigate(`/product/${p.id}`)}
                      className="text-left font-display text-white text-base leading-snug hover:text-[#FFC61A] transition-colors mb-2"
                    >
                      {p.name}
                    </button>
                    <p className="text-xs text-slate-500 line-clamp-2 mb-4">{p.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-display text-xl text-[#FFC61A]">{usd(p.price)}</span>
                      <button
                        data-testid={`bd-product-add-${i}`}
                        onClick={() => { add(p); toast.success(`${p.name} — ${t("added_to_cart")}`); }}
                        className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-5 py-2.5 transition-colors duration-300"
                      >
                        <ShoppingCart size={14} /> {t("add_to_cart")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      )}
    </section>
  );
}
